export type MetaDataArgs = {
  organization?: { orgName: string };
  jobTitle?: string;
  roundName?: string;
};

export function getPageMetaData(
  page:
    | "signin"
    | "signup"
    | "listing"
    | "requirements"
    | "notfound"
    | "airound"
    | "result"
    | "onlineinterview"
    | "forgotpassowrd"
    | "resetpassword"
    | string,
  args?: MetaDataArgs
): { title: string; description: string } {
  switch (page) {
    case "signin": {
      const orgName = args?.organization?.orgName;
      return {
        title: orgName
          ? `${orgName} | Candidate | Sign In`
          : "QualifyMe | Candidate | Sign In",
        description:
          "Sign in to your QualifyMe candidate account to access personalized job matches, track your applications, and manage your career growth efficiently.",
      };
    }

    case "signup": {
      const orgName = args?.organization?.orgName;
      return {
        title: orgName
          ? `${orgName} | Candidate | Sign Up`
          : "QualifyMe | Candidate | Sign Up",
        description:
          "Create your QualifyMe candidate account to explore job opportunities, get matched with roles that fit your profile, and grow your career with confidence.",
      };
    }

    case "listing": {
      const orgName = args?.organization?.orgName;
      return {
        title: orgName
          ? `${orgName} | Job Openings`
          : "QualifyMe | Job Openings",
        description:
          "Browse the latest job openings on QualifyMe. Discover roles tailored to your skills and apply directly to top companies.",
      };
    }

    case "requirements": {
      const orgName = args?.organization?.orgName;
      const jobTitle = args?.jobTitle;
      return {
        title:
          jobTitle && orgName
            ? `Apply for ${jobTitle} at ${orgName} | QualifyMe`
            : jobTitle
            ? `Apply for ${jobTitle} | QualifyMe`
            : orgName
            ? `${orgName} | QualifyMe | Job post details`
            : "QualifyMe | Job Requirements",
        description:
          jobTitle && orgName
            ? `Explore the requirements for the ${jobTitle} position at ${orgName}. See if you qualify and apply now on QualifyMe!`
            : jobTitle
            ? `Explore the requirements for the ${jobTitle} position. See if you qualify and apply now on QualifyMe!`
            : orgName
            ? `See job requirements and apply at ${orgName} on QualifyMe.`
            : "See job requirements and apply for your next opportunity on QualifyMe.",
      };
    }

    case "airound": {
      const orgName = args?.organization?.orgName;
      const roundName = args?.roundName;
      return {
        title:
          roundName && orgName
            ? `${roundName} | ${orgName}`
            : roundName
            ? `${roundName} | QualifyMe`
            : orgName
            ? `${orgName} | AI Assessment Round`
            : "AI Assessment Round | QualifyMe",
        description:
          "Complete this AI-powered assessment round designed to evaluate your skills and qualifications for the position. Powered by QualifyMe's smart evaluation engine.",
      };
    }

    case "result": {
      const orgName = args?.organization?.orgName;
      return {
        title: orgName
          ? `Assessment Results | ${orgName}`
          : "Assessment Results | QualifyMe",
        description:
          "View your AI evaluation results and performance insights. Understand your strengths and areas for improvement with detailed feedback from your assessment round.",
      };
    }

    case "onlineinterview": {
      const orgName = args?.organization?.orgName;
      return {
        title: orgName
          ? `${orgName} | Online Interview`
          : "Online Interview | QualifyMe",
        description: orgName
          ? `Attend your scheduled online interview with the hiring team at ${orgName}, or select an available time slot to proceed.`
          : "Join your scheduled online interview or choose an available time slot to connect with the hiring team on QualifyMe.",
      };
    }

    case "forgotpassowrd": {
      const orgName = args?.organization?.orgName;
      return {
        title: orgName
          ? `${orgName} | Candidate | Forgot Password`
          : "QualifyMe | Candidate | Forgot Password",
        description: orgName
          ? `Reset your QualifyMe candidate account password to regain access to job opportunities at ${orgName}.`
          : "Reset your QualifyMe candidate account password to regain access to job opportunities and personalized recommendations.",
      };
    }

    case "resetpassword": {
      const orgName = args?.organization?.orgName;
      return {
        title: orgName
          ? `${orgName} | Candidate | Reset Password`
          : "QualifyMe | Candidate | Reset Password",
        description: orgName
          ? `Set a new password for your QualifyMe candidate account to continue exploring job opportunities at ${orgName}.`
          : "Set a new password for your QualifyMe candidate account to continue exploring job opportunities and managing your career growth.",
      };
    }

    case "notfound":
      return {
        title: "QualifyMe | Candidate | Page Not Found",
        description:
          "Page not found. Navigate back to explore jobs, sign in, or learn more about QualifyMe.",
      };

    default:
      return {
        title: "QualifyMe | Candidate",
        description:
          "QualifyMe - Your platform for job matching and career growth.",
      };
  }
}
